import React, { useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { CardActionArea } from '@mui/material';
import PlayPauseOuterCircle from '../../assets/playPauseCircleOuter.svg';
import PlayPauseInnerCircle from '../../assets/playPauseCircleInner.svg';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

export default function VideoCard({ video, index, isPlaying, onPlayPause }) {
	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const videoRef = useRef(null);

	// Extract YouTube video ID from URL
	const getYouTubeVideoId = (url) => {
		const regExp =
			/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
		const match = url.match(regExp);
		return match && match[2].length === 11 ? match[2] : null;
	};

	const isYouTube =
		video.type === 'youtube' ||
		(video.source && video.source.includes('youtube.com'));
	const youtubeId = isYouTube
		? video.youtubeId || getYouTubeVideoId(video.source)
		: null;

	const getVideoMimeType = (source) => {
		if (!source) return 'video/mp4';
		const lower = source.toLowerCase();
		if (lower.endsWith('.mov')) return 'video/quicktime';
		if (lower.endsWith('.webm')) return 'video/webm';
		return 'video/mp4';
	};

	const toggleFullscreen = () => {
		if (videoRef.current) {
			videoRef.current.requestFullscreen();
		}
	};

	const togglePlayPause = () => {
		if (isYouTube) {
			// For YouTube videos, we'll handle play/pause via the iframe API or redirect to YouTube
			if (youtubeId) {
				window.open(`https://www.youtube.com/watch?v=${youtubeId}`, '_blank');
			}
		} else {
			// Regular video handling
			if (videoRef.current.paused) {
				videoRef.current.play();
				onPlayPause(index);
			} else {
				videoRef.current.pause();
				onPlayPause(null);
			}
		}
	};

	useEffect(() => {
		if (!isPlaying && !isYouTube && videoRef.current) {
			videoRef.current.pause();
		}
	}, [isPlaying, isYouTube]);

	const handleLoadedMetadata = () => {
		if (videoRef.current && videoRef.current.paused) {
			videoRef.current.controls = false;
		}
	};

	useEffect(() => {
		if (isYouTube) return;

		const handleFullscreenChange = () => {
			if (videoRef.current) {
				if (!document.fullscreenElement) {
					videoRef.current.controls = false;
					videoRef.current.style.pointerControls = 'none';
				} else {
					videoRef.current.style.pointerControls = '';
				}
			}
		};

		const handleWebkitFullscreenChange = () => {
			if (videoRef.current) {
				if (!document.fullscreenElement) {
					document.exitPointerLock();
					videoRef.current.controls = false;
					videoRef.current.style.pointerControls = 'none';
				} else {
					document.documentElement.requestPointerLock();
					videoRef.current.style.pointerControls = '';
				}
			}
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		document.addEventListener(
			'webkitfullscreenchange',
			handleWebkitFullscreenChange
		);

		if (videoRef.current) {
			videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
		}

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			document.removeEventListener(
				'webkitfullscreenchange',
				handleWebkitFullscreenChange
			);
			if (videoRef.current) {
				videoRef.current.removeEventListener(
					'loadedmetadata',
					handleLoadedMetadata
				);
			}
		};
	}, [isYouTube]);

	const renderVideoContent = () => {
		if (isYouTube && youtubeId) {
			const thumbnailSrc =
				video.thumbnail && !video.thumbnail.includes('placeholder')
					? video.thumbnail
					: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

			return (
				<div className='video-container'>
					<img
						className='video youtube-thumbnail'
						src={thumbnailSrc}
						alt={video.title}
					/>
					<button onClick={togglePlayPause} className='play-btn'>
						<img
							src={PlayPauseOuterCircle}
							alt='play-btn-outer-circle'
							className='play-btn-outer-circle'
						/>
						<img
							src={PlayPauseInnerCircle}
							alt='play-btn-inner-circle'
							className='play-btn-inner-circle'
						/>
						<span>
							<PlayArrowIcon fontSize='large' />
						</span>
					</button>
				</div>
			);
		} else {
			// Regular video
			return (
				<div className='video-container'>
					<video
						className='video'
						poster={video.thumbnail}
						controls={false}
						ref={videoRef}
						onEnded={() => onPlayPause(null)}>
						<source
							src={video.source}
							type={video.mimeType || getVideoMimeType(video.source)}
						/>
						Your browser does not support the video tag.
					</video>
					<button
						onClick={togglePlayPause}
						className={`play-btn ${isPlaying && !isMobile ? 'pause-btn' : ''}`}>
						<img
							src={PlayPauseOuterCircle}
							alt='play-btn-outer-circle'
							className='play-btn-outer-circle'
						/>
						<img
							src={PlayPauseInnerCircle}
							alt='play-btn-inner-circle'
							className='play-btn-inner-circle'
						/>
						<span>
							{isPlaying && !isMobile ? (
								<PauseIcon fontSize='large' />
							) : (
								<PlayArrowIcon fontSize='large' />
							)}
						</span>
					</button>

					<button
						onClick={toggleFullscreen}
						className='full-screen-btn'
						style={{ opacity: isMobile ? 0 : 1 }}>
						<FullscreenIcon className='full-screen-icon' />
					</button>
				</div>
			);
		}
	};

	return (
		<Card
			sx={{
				minWidth: 300,
				maxWidth: 300,
				marginRight: 5,
				background: '#02030B',
				color: '#FFFFFF',
				textAlign: 'left',
				minHeight: 265,
				borderRadius: 2,
				display: 'flex',
				alignItems: 'flex-start',
			}}>
			<CardActionArea
				sx={{
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
				}}>
				{renderVideoContent()}
				<CardContent sx={{ width: '100%', pt: 1.5, pb: 1.5 }}>
					<Typography variant='h5' component='div' sx={{ lineHeight: 1.2 }}>
						{video.title}
					</Typography>
					{video.details && (
						<Typography variant='body1' sx={{ mt: 0.5, color: '#b0b0b0' }}>
							{video.details}
						</Typography>
					)}
				</CardContent>
			</CardActionArea>
		</Card>
	);
}
